// Portail patient (cahier des charges labo, section 17) : page publique,
// sans compte, ouverte via le lien recu par email (section 16). Meme
// principe que VerifyPrescription.js (token dans l'URL, aucune
// authentification) mais pour consulter ses propres rendez-vous plutot que
// verifier un document. Page volontairement autonome (styles locaux, pas de
// dependance a HopitalDashboard.js) : elle sert un public totalement
// different (patients, pas de session professionnelle).
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Phone, User, HelpCircle, Home, Loader, AlertCircle, MessageCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = { confirme: "Confirme", annule: "Annule", termine: "Termine", no_show: "Absent" };
const STATUS_COLORS = {
  confirme: ["#E6F1FB", "#185FA5"],
  annule: ["#F1F5F9", "#94A3B8"],
  termine: ["#DCFCE7", "#166534"],
  no_show: ["#FEF2E8", "#B45309"],
};

function fmtDateFR(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  } catch (e) { return iso; }
}

export default function PatientPortalLab() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("accueil");
  // Messagerie avec l'etablissement (cahier des charges labo, section 18) :
  // fil charge a la demande (pas dans l'appel /api/portal/{token} ci-dessus,
  // qui reste decouple de ce module).
  const [messages, setMessages] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  // Confirmation obligatoire (cahier des charges labo, section 23) : la
  // seule autre ecriture exposee par ce portail avec la messagerie (§18) —
  // voir routes/patient_portal.py pour le raisonnement.
  const [confirmingId, setConfirmingId] = useState(null);
  const [confirmError, setConfirmError] = useState("");

  // useCallback (plutot que la fonction simple d'origine + un commentaire
  // d'exclusion de la regle exhaustive-deps de ESLint) : une version
  // d'outil de build a rendu ce commentaire d'exclusion fragile (le build de
  // production a echoue avec "Definition for rule ... was not found" alors
  // que ce n'etait qu'un avertissement en local) - satisfaire la regle
  // proprement, sans avoir a la desactiver, est plus robuste dans la duree.
  const loadData = useCallback(() => {
    axios.get(`${API_URL}/api/portal/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(() => {
    loadData();
  }, [loadData]);

  const confirmAppointment = async (appointmentId) => {
    setConfirmingId(appointmentId); setConfirmError("");
    try {
      await axios.post(`${API_URL}/api/portal/${token}/appointments/${appointmentId}/confirm`);
      loadData();
    } catch (e) {
      setConfirmError(e.response?.data?.detail || "Impossible de confirmer ce rendez-vous");
    } finally { setConfirmingId(null); }
  };

  const loadMessages = useCallback(() => {
    setMsgLoading(true); setMsgError("");
    axios.get(`${API_URL}/api/portal/${token}/messages`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => setMsgError("Impossible de charger vos messages"))
      .finally(() => setMsgLoading(false));
  }, [token]);
  useEffect(() => {
    if (tab === "messages" && messages === null) loadMessages();
  }, [tab, messages, loadMessages]);
  const sendMessage = async () => {
    const body = msgBody.trim();
    if (!body) return;
    setMsgSending(true); setMsgError("");
    try {
      await axios.post(`${API_URL}/api/portal/${token}/messages`, { body });
      setMsgBody(""); loadMessages();
    } catch (e) { setMsgError(e.response?.data?.detail || "Erreur lors de l'envoi du message"); }
    finally { setMsgSending(false); }
  };
  const viewAttachment = async (m) => {
    try {
      const r = await axios.get(`${API_URL}/api/portal/${token}/messages/${m.message_id}/attachment`);
      window.open(r.data.url, "_blank");
    } catch (e) { setMsgError("Impossible d'ouvrir la piece jointe"); }
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <Loader size={40} style={{ color: "#185FA5", animation: "spin 1s linear infinite" }} />
          <p style={S.muted}>Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <AlertCircle size={48} color="#DC2626" />
          <h1 style={S.title}>Lien invalide ou expire</h1>
          <p style={S.muted}>Ce lien n'est plus valide. Contactez votre laboratoire pour en obtenir un nouveau.</p>
        </div>
      </div>
    );
  }

  const { patient, clinic, appointments } = data;
  const todayStr = new Date().toISOString().slice(0, 10);
  const next = appointments
    .filter((a) => a.status === "confirme" && (a.date > todayStr || (a.date === todayStr)))
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))[0];

  const MENU = [
    { id: "accueil", label: "Accueil", icon: Home },
    { id: "rdv", label: "Rendez-vous", icon: Calendar },
    { id: "messages", label: "Messages", icon: MessageCircle },
    { id: "profil", label: "Informations personnelles", icon: User },
    { id: "aide", label: "Aide", icon: HelpCircle },
  ];

  return (
    <div style={S.page}>
      <div style={{ ...S.card, maxWidth: 640, textAlign: "left" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5 }}>
          {clinic?.name || "Votre laboratoire"}
        </div>
        <h1 style={{ ...S.title, textAlign: "left", margin: "6px 0 18px" }}>Bonjour {patient?.first_name || ""}</h1>

        <div style={S.tabs}>
          {MENU.map((m) => {
            const Icon = m.icon;
            return (
              <button key={m.id} onClick={() => setTab(m.id)}
                style={{ ...S.tabBtn, ...(tab === m.id ? S.tabBtnActive : {}) }}>
                <Icon size={15} style={{ marginRight: 6, verticalAlign: -3 }} />{m.label}
              </button>
            );
          })}
        </div>

        {tab === "accueil" && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginBottom: 10 }}>Votre prochain rendez-vous</div>
            {confirmError && <div style={{ ...S.emptyCard, color: "#DC2626", marginBottom: 10 }}>{confirmError}</div>}
            {next ? (
              <div style={S.nextCard}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", textTransform: "capitalize" }}>{fmtDateFR(next.date)}</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: "#185FA5", margin: "4px 0" }}>{next.time}</div>
                <div style={{ fontSize: 14, color: "#475569" }}>{clinic?.name || "Votre laboratoire"}</div>
                {next.act_type_name && <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>{next.act_type_name}</div>}
                {next.confirmation_status === "en_attente" && (
                  <>
                    <div style={{ fontSize: 13, color: "#B45309", marginTop: 10, fontWeight: 600 }}>Merci de confirmer votre presence</div>
                    <button onClick={() => confirmAppointment(next.appointment_id)} disabled={confirmingId === next.appointment_id} style={S.primaryBtn}>
                      {confirmingId === next.appointment_id ? "Confirmation..." : "Confirmer ma presence"}
                    </button>
                  </>
                )}
                {next.confirmation_status === "confirme" && (
                  <div style={{ fontSize: 13, color: "#166534", marginTop: 10, fontWeight: 600 }}>✓ Rendez-vous confirme</div>
                )}
                <button onClick={() => setTab("rdv")} style={{ ...S.primaryBtn, ...(next.confirmation_status === "en_attente" ? { marginTop: 8, background: "transparent", color: "#185FA5", border: "1px solid #185FA5" } : {}) }}>Voir le rendez-vous</button>
              </div>
            ) : (
              <div style={S.emptyCard}>Vous n'avez aucun rendez-vous a venir.</div>
            )}
          </div>
        )}

        {tab === "rdv" && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginBottom: 10 }}>Tous vos rendez-vous</div>
            {confirmError && <div style={{ ...S.emptyCard, color: "#DC2626", marginBottom: 10 }}>{confirmError}</div>}
            {appointments.length === 0 && <div style={S.emptyCard}>Aucun rendez-vous enregistre.</div>}
            {[...appointments].reverse().map((a) => {
              const sc = STATUS_COLORS[a.status] || ["#F1F5F9", "#64748B"];
              return (
                <div key={a.appointment_id} style={S.rdvRow}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A", textTransform: "capitalize" }}>{fmtDateFR(a.date)} · {a.time}</div>
                    {a.act_type_name && <div style={{ fontSize: 13, color: "#64748B" }}>{a.act_type_name}</div>}
                    {a.confirmation_status === "en_attente" && (
                      <button onClick={() => confirmAppointment(a.appointment_id)} disabled={confirmingId === a.appointment_id} style={{ marginTop: 6, fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1px solid #185FA5", background: "#fff", color: "#185FA5", cursor: "pointer" }}>
                        {confirmingId === a.appointment_id ? "..." : "Confirmer"}
                      </button>
                    )}
                  </div>
                  <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1], whiteSpace: "nowrap" }}>
                    {STATUS_LABELS[a.status] || a.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {tab === "messages" && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginBottom: 10 }}>Messagerie avec {clinic?.name || "votre laboratoire"}</div>
            {msgError && <div style={{ ...S.emptyCard, color: "#DC2626", marginBottom: 10, textAlign: "left" }}>{msgError}</div>}
            {msgLoading && <div style={S.emptyCard}>Chargement...</div>}
            {!msgLoading && messages && messages.length === 0 && <div style={S.emptyCard}>Aucun message pour le moment.</div>}
            {!msgLoading && messages && messages.map((m) => (
              <div key={m.message_id} style={{ marginBottom: 10, textAlign: m.sender === "patient" ? "right" : "left" }}>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 2 }}>{m.sender === "patient" ? "Vous" : (m.sender_name || clinic?.name || "Laboratoire")}</div>
                <div style={{
                  display: "inline-block", maxWidth: "85%", padding: "9px 13px", borderRadius: 12, fontSize: 14, textAlign: "left",
                  background: m.sender === "patient" ? "#185FA5" : "#F1F5F9", color: m.sender === "patient" ? "#fff" : "#0F172A",
                }}>
                  {m.body && <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>}
                  {m.attachment && (
                    <div style={{ marginTop: m.body ? 6 : 0 }}>
                      <button onClick={() => viewAttachment(m)} style={{ fontSize: 13, background: "none", border: "none", cursor: "pointer", color: m.sender === "patient" ? "#E0F2FE" : "#185FA5", textDecoration: "underline", padding: 0 }}>
                        📎 {m.attachment.filename}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <textarea placeholder="Ecrire un message..." value={msgBody} onChange={(e) => setMsgBody(e.target.value)}
                style={{ flex: 1, minHeight: 48, padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, resize: "vertical", fontFamily: "inherit" }} />
              <button onClick={sendMessage} disabled={msgSending || !msgBody.trim()}
                style={{ ...S.primaryBtn, marginTop: 0, alignSelf: "flex-end", opacity: (msgSending || !msgBody.trim()) ? 0.6 : 1 }}>
                {msgSending ? "..." : "Envoyer"}
              </button>
            </div>
          </div>
        )}

        {tab === "profil" && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginBottom: 10 }}>Vos informations</div>
            <ProfilRow label="Nom complet" value={`${patient?.first_name || ""} ${patient?.last_name || ""}`.trim()} />
            <ProfilRow label="Date de naissance" value={patient?.date_of_birth || "—"} />
            <ProfilRow label="Telephone" value={patient?.phone || "—"} />
            <ProfilRow label="Email" value={patient?.email || "—"} />
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 14, lineHeight: 1.6 }}>
              Une erreur dans vos informations ? Contactez directement votre laboratoire — ces informations ne sont pas modifiables depuis cette page.
            </div>
          </div>
        )}

        {tab === "aide" && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748B", marginBottom: 10 }}>Contacter votre laboratoire</div>
            <div style={S.emptyCard}>
              <div style={{ fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>{clinic?.name || "Votre laboratoire"}</div>
              {clinic?.phone && <div style={{ fontSize: 14, color: "#334155", marginBottom: 4 }}><Phone size={14} style={{ marginRight: 6, verticalAlign: -2 }} />{clinic.phone}</div>}
              {clinic?.address && <div style={{ fontSize: 14, color: "#334155" }}><MapPin size={14} style={{ marginRight: 6, verticalAlign: -2 }} />{clinic.address}</div>}
              {!clinic?.phone && !clinic?.address && <div style={{ fontSize: 13, color: "#94A3B8" }}>Coordonnees non renseignees.</div>}
            </div>
          </div>
        )}

        <div style={S.footer}>Portail patient · Tabbibak.dz</div>
      </div>
    </div>
  );
}

function ProfilRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #F1F5F9" }}>
      <span style={{ fontSize: 13, color: "#64748B" }}>{label}</span>
      <span style={{ fontSize: 14, color: "#0F172A", fontWeight: 500 }}>{value || "—"}</span>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh", display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "40px 16px", background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  card: {
    background: "#ffffff", borderRadius: 16, padding: "32px 28px", maxWidth: 480, width: "100%",
    boxShadow: "0 20px 50px rgba(0,0,0,0.08)", textAlign: "center",
  },
  title: { fontSize: 22, margin: "12px 0 8px", color: "#0F172A" },
  muted: { fontSize: 14, color: "#64748B", marginTop: 10, lineHeight: 1.5 },
  tabs: { display: "flex", flexWrap: "wrap", gap: 6, borderBottom: "1px solid #E2E8F0", paddingBottom: 12 },
  tabBtn: {
    padding: "7px 12px", border: "1px solid #E2E8F0", borderRadius: 20, background: "#fff",
    color: "#64748B", fontSize: 13, cursor: "pointer",
  },
  tabBtnActive: { background: "#185FA5", borderColor: "#185FA5", color: "#fff", fontWeight: 600 },
  nextCard: { background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: 20, textAlign: "center" },
  emptyCard: { background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, fontSize: 13, color: "#94A3B8", textAlign: "left" },
  rdvRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 14px", border: "1px solid #F1F5F9", borderRadius: 10, marginBottom: 8,
  },
  primaryBtn: {
    marginTop: 14, padding: "9px 20px", background: "#185FA5", color: "#fff", border: "none",
    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  footer: { marginTop: 24, paddingTop: 14, borderTop: "1px solid #E2E8F0", color: "#94A3B8", fontSize: 12 },
};
